import React, {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  useRef,
  useState,
} from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'

import { newDeviceInfo } from '../../../tools/mockData'
import {
  ADD_KEY_MAPPINGS_PERMISSION,
  UPDATE_KEY_MAPPINGS_PERMISSION,
} from '../../redux/actions/actionTypes'
import * as keyMappingActions from '../../redux/actions/keyMappingActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { IRootState } from '../../redux/reducers'
import {
  DeviceKeyMappingInfo,
  UserPermissionsState,
} from '../../redux/reducers/initialState'
import * as constants from '../../utils'
import ExpandableRow from '../common/ExpandableRow'
import GenericModal from '../common/GenericModal'
import TOverlayTrigger from '../common/OverlayTrigger'
import { usePermissions } from '../common/permission/usePermissions'
import TBody from '../common/TBody'
import TDeleteLink from '../common/TDeleteLink'
import TEditLink from '../common/TEditLink'
import TFooter from '../common/TFooter'
import TRowEmpty from '../common/TRowEmpty'
import { isScreenSizeSmall } from '../common/utils'

import KeyMappingForm from './KeyMappingForm'
import KeyMappingUpload from './KeyMappingUpload'

import '../../css/common/KeyList.css'

type KeyMappingsListProps = {
  keyMappings: DeviceKeyMappingInfo[]
  onDeleteClick: (keyMapping: DeviceKeyMappingInfo) => void
  onSearchClick: () => void
  onLoadMoreClick: () => void
  handleChange: ChangeEventHandler<HTMLInputElement>
  handleBlur: FocusEventHandler<HTMLInputElement>
  loading: boolean
  keyMappingsExists: boolean
  searchMappingsRequest: DeviceKeyMappingInfo
  setDataChanged?: Dispatch<React.SetStateAction<boolean>>
}

const KeyMappingsList = ({
  keyMappings,
  onLoadMoreClick,
  onDeleteClick,
  onSearchClick,
  loading,
  handleChange,
  handleBlur,
  searchMappingsRequest,
  keyMappingsExists,
}: KeyMappingsListProps) => {
  const dispatch = useAppDispatch()
  const { checkPermissions } = usePermissions()
  const keyMappingsPerPage = constants.recordsPerPage
  const keyMappingsPageNum: number = useSelector(
    (state: IRootState) => state.keyMappingsPageNum,
  )
  const userPermissions: UserPermissionsState | null = useSelector(
    (state: IRootState) => state.userPermissions,
  )
  const isAuthorized: boolean = useSelector(
    (state: IRootState) => state.identity != null,
  )
  const disableLoadMore: boolean = useSelector(
    (state: IRootState) =>
      state.searchMappingRequest.continuationToken == null ||
      state.searchMappingRequest.continuationToken === '',
  )

  const disableGetKeyMappings =
    loading ||
    !(
      isAuthorized &&
      userPermissions !== null &&
      userPermissions.hasGetPermissionsAccess
    )
  const disableAddKeyMappings =
    loading ||
    !(
      isAuthorized &&
      userPermissions !== null &&
      userPermissions.hasAddPermissionsAccess
    )
  const disableEditKeyMappings = !(
    isAuthorized &&
    userPermissions !== null &&
    userPermissions.hasUpdatePermissionsAccess
  )
  const disableDeleteKeyMappings = !(
    isAuthorized &&
    userPermissions !== null &&
    userPermissions.hasDeletePermissionsAccess
  )
  const disableUploadKeyMappings =
    disableAddKeyMappings || disableEditKeyMappings

  const totalPages = Math.ceil(keyMappings.length / keyMappingsPerPage)
  const showingText = `Showing ${
    (keyMappingsPageNum - 1) * keyMappingsPerPage + 1
  }  to  ${Math.min(
    keyMappings.length,
    keyMappingsPageNum * keyMappingsPerPage,
  )}  of ${keyMappings.length}`

  const handlePageChange = (event, page) =>
    dispatch(keyMappingActions.updateKeyMappingsPageNum(page))

  const [formData, setFormData] = useState<DeviceKeyMappingInfo>(newDeviceInfo)
  const [uploadData, setUploadData]: any = useState({})
  const [formReady, setFormReady] = useState(false)
  const [dataChanged, setDataChanged] = useState(false)
  const refFormData: any = useRef()
  const refUploadData: any = useRef()
  const refValidateData: any = useRef()
  refFormData.current = formData
  refUploadData.current = uploadData
  const validateSaveAddKeyMappingModal = () => {
    refValidateData.current.validate()
    return formReady
  }
  const handleSaveAddKeyMappingModal = () => {
    checkPermissions(store.getState(), ADD_KEY_MAPPINGS_PERMISSION)
    dispatch(keyMappingActions.addKeyMapping(refFormData.current))
    setFormData(newDeviceInfo)
  }
  const handleCloseAddKeyMappingModal = () => {
    setFormData(newDeviceInfo)
  }
  const handleSubmitUploadKeyMappingModal = () => {
    checkPermissions(store.getState(), ADD_KEY_MAPPINGS_PERMISSION)
    checkPermissions(store.getState(), UPDATE_KEY_MAPPINGS_PERMISSION)

    dispatch(keyMappingActions.uploadKeyMapping(refUploadData.current))
  }

  const handleEnterKeyPress = (event) => {
    if (event.key == 'Enter') {
      return onSearchClick()
    }
  }

  const displayKeyMappingRow = (keyMapping) => {
    const lastUsedDate = new Date(keyMapping.lastUsed).toLocaleDateString()
    const updatedOnDate = new Date(keyMapping.updatedOn).toLocaleDateString()
    const lastUsedStr = new Date(keyMapping.lastUsed).toLocaleString()
    const updatedOnStr = new Date(keyMapping.updatedOn).toLocaleString()

    return (
      <ExpandableRow rowIndex={keyMapping.deviceId}>
        {(isExpanded, toggleExpansion) => (
          <tr
            key={keyMapping.deviceId}
            className={isExpanded() ? 'expandedRow' : 'tableRow'}
          >
            <td className="tableCell" onClick={toggleExpansion}>
              {keyMapping.deviceId}
            </td>
            <td className="tableCell" onClick={toggleExpansion}>
              {keyMapping.userEmailAddress}
            </td>
            <td className="tableCell" onClick={toggleExpansion}>
              {keyMapping.adGroupName}
            </td>
            <TOverlayTrigger
              tooltipId="tooltip-lastUsed"
              tooltipVal={
                keyMapping.lastUsed == null || keyMapping.lastUsed == ''
                  ? null
                  : lastUsedStr
              }
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {keyMapping.lastUsed == null ? '' : lastUsedDate}
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-importedOn"
              tooltipVal={new Date(keyMapping.importedOn).toLocaleString()}
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {new Date(keyMapping.importedOn).toLocaleDateString()}
                </td>
              }
            />
            <TOverlayTrigger
              tooltipId="tooltip-updatedOn"
              tooltipVal={
                keyMapping.updatedOn == null || keyMapping.updatedOn == ''
                  ? null
                  : updatedOnStr
              }
              tooltipChild={
                <td className="tableCell" onClick={toggleExpansion}>
                  {keyMapping.updatedOn == null ? '' : updatedOnDate}
                </td>
              }
            />
            <td>
              <TEditLink
                disable={disableEditKeyMappings}
                pathName={'/keymappings/' + keyMapping.rowKey}
              />
            </td>
            <td>
              <TDeleteLink
                disable={disableDeleteKeyMappings}
                title="Delete Permission"
                onDelete={() => onDeleteClick(keyMapping)}
                itemName={keyMapping.deviceId}
                showNoteSoftDelete={false}
              />
            </td>
          </tr>
        )}
      </ExpandableRow>
    )
  }

  const TRow = () =>
    keyMappingsExists ? (
      <>
        {keyMappings
          .slice(
            (keyMappingsPageNum - 1) * keyMappingsPerPage,
            keyMappingsPageNum * keyMappingsPerPage,
          )
          .map((keyMapping) => {
            return displayKeyMappingRow(keyMapping)
          })}
      </>
    ) : (
      <TRowEmpty colSpan="8" emptyRowText="No Device Permissions Found" />
    )

  return (
    <>
      <div className="scrollable responsive-font">
        <Container fluid>
          <Row className="g-3">
            <Col xs={12} lg={2} className="mt-4">
              <input
                name="deviceId"
                type="text"
                placeholder="Device ID"
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={searchMappingsRequest.deviceId}
                disabled={
                  searchMappingsRequest.userEmailAddress.length > 0 ||
                  searchMappingsRequest.adGroupName.length > 0
                }
              />
            </Col>
            <Col xs={12} lg={2} className="mt-4">
              <input
                name="userEmailAddress"
                type="text"
                placeholder="Email Address"
                className="form-control responsive-font"
                disabled={searchMappingsRequest.deviceId.length > 0}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={searchMappingsRequest.userEmailAddress}
              />
            </Col>
            <Col xs={12} lg={2} className="mt-4">
              <input
                name="adGroupName"
                type="text"
                placeholder="Azure AD Group"
                className="form-control responsive-font"
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleEnterKeyPress}
                value={searchMappingsRequest.adGroupName}
                disabled={searchMappingsRequest.deviceId.length > 0}
              />
            </Col>
            <Col xs={12} lg={6} className="mt-4">
              <button
                onClick={onSearchClick}
                disabled={disableGetKeyMappings}
                className={
                  `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                  'responsive-button btn text-nowrap spacing-right' +
                  (disableGetKeyMappings
                    ? ' btn-outline-primary'
                    : ' btn-primary')
                }
              >
                Get Permissions
              </button>{' '}
              <GenericModal
                disableModal={disableAddKeyMappings}
                title="Add Permission"
                preOk={validateSaveAddKeyMappingModal}
                onOk={handleSaveAddKeyMappingModal}
                onClose={handleCloseAddKeyMappingModal}
                modalBody={
                  <KeyMappingForm
                    ref={refValidateData}
                    entityData={formData}
                    setEntityData={setFormData}
                    setFormReady={setFormReady}
                    setDataChanged={setDataChanged}
                  />
                }
                buttonOkText="Save"
              >
                <button
                  disabled={disableAddKeyMappings}
                  className={
                    `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                    'responsive-button btn text-nowrap spacing-right' +
                    (disableAddKeyMappings
                      ? ' btn-outline-success'
                      : ' btn-success')
                  }
                >
                  Add
                </button>
              </GenericModal>{' '}
              <GenericModal
                disableModal={disableUploadKeyMappings}
                disableOkButton={!formReady}
                title="Upload File"
                onOk={handleSubmitUploadKeyMappingModal}
                modalBody={
                  <KeyMappingUpload
                    setUploadData={setUploadData}
                    setFormReady={setFormReady}
                  />
                }
                buttonOkText="Upload"
              >
                <button
                  disabled={disableUploadKeyMappings}
                  className={
                    `${isScreenSizeSmall() ? 'mt-4 ' : ''}` +
                    'responsive-button btn text-nowrap' +
                    (disableUploadKeyMappings
                      ? ' btn-outline-success'
                      : ' btn-success')
                  }
                >
                  Upload Permissions
                </button>
              </GenericModal>
            </Col>
          </Row>
        </Container>

        <table className="table table-hover responsive-font mt-3">
          <thead>
            <tr className="border-top">
              <th>Device ID</th>
              <th>Email Address</th>
              <th>Azure AD Group</th>
              <th>Last Used</th>
              <th>Created On</th>
              <th>Modified On</th>
              <th />
              <th />
            </tr>
          </thead>
          <TBody isLoading={loading} TRow={TRow} colSpan="8" />
        </table>
      </div>
      <TFooter
        show={keyMappings.length > 0 && !loading}
        showingText={showingText}
        pageNum={keyMappingsPageNum}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        allowLoadMore={!disableLoadMore}
        onLoadMoreClick={onLoadMoreClick}
      />
    </>
  )
}

export default KeyMappingsList
